/**
 * Discount-code capture + persistence (`?ref=` on any URL).
 *
 * Two kinds of code arrive through the same door:
 *
 *  - **Personal referrals** — Sam's "share with neighbors" program.
 *    An existing customer pastes
 *    `https://protestingpropertytaxes.com/?ref=NB-A2B5C` to a neighbor.
 *    These carry a `referrer_email`, which is what makes
 *    `is_referral` true server-side.
 *  - **Plain promo codes** — a campaign/event code with no referrer
 *    (a QR code on a flyer, a link handed out at an event). Same
 *    capture, same auto-apply; only the surrounding copy differs,
 *    because "a neighbor shared this with you" would be a lie.
 *
 * Either way the first hit on the site captures the code into
 * localStorage so it survives the multi-page funnel (home → /verify
 * → /submit), and the email/checkout step auto-applies it.
 *
 * The slug format is loose on purpose — the source of truth is the
 * server-side `validate` endpoint, not a regex here. We accept any
 * non-empty alphanumeric+hyphen string and let the API reject it
 * if invalid.
 */

import axios from "axios";

import { API_URL } from "../constants";

// Storage-key version is bumped whenever the stored record's shape
// changes incompatibly.  v1 (the original) stored
// ``discountAmountCents`` which never populated correctly because
// the API field is ``discount_amount_dollars``; visitors who hit
// the buggy version still have the broken record in localStorage.
// Bumping the key here orphans v1 data so a fresh validate runs
// the next time they hit ?ref= URL — and the banner / modal /
// auto-apply flow finally fires.
const STORAGE_KEY = "pxn_referral_code_v2";

/** Read the slug from `?ref=...` on the current location, or
 * `null` when absent. */
function readFromQuery() {
  if (typeof window === "undefined" || !window.location) return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("ref");
    if (!raw) return null;
    const trimmed = raw.trim();
    // Light shape check — the API does the real validation. This
    // just guards against `?ref=<script>` style noise.
    if (!/^[A-Za-z0-9-]{3,50}$/.test(trimmed)) return null;
    return trimmed;
  } catch (_e) {
    return null;
  }
}

/** Persist the slug in localStorage. Stores the validated public
 * record so subsequent reads don't have to re-hit the API. */
export function setStoredReferral(record) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (_e) {
    // Storage quota / private mode; ignore.
  }
}

/** Read the stored referral record (if any). Returns `null` when
 * nothing's stored or the JSON is malformed. */
export function getStoredReferral() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
}

/** Drop the stored record. Called after the discount has been
 * locked into an order so it doesn't keep auto-applying. */
export function clearStoredReferral() {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (_e) {
    // ignore
  }
}

/** Describe a captured code as display copy, or `null` when the
 *  record carries no usable discount.
 *
 *  Precedence deliberately mirrors the backend's
 *  ``DiscountCode.apply_to``: a fixed dollar amount WINS whenever it
 *  is set, and the percentage only applies otherwise. This matters
 *  because ``discount_percentage`` has a model default of 100, so a
 *  code hand-created in the admin with only ``discount_amount_dollars``
 *  still arrives here with ``discountPercentage: 100``. Reading the
 *  percentage first would render "a free report" for what is really
 *  "$20 off". (Codes minted by referrals.py set the percentage to 0
 *  explicitly, which is why that path never tripped over this.) */
export function describeDiscount(record) {
  if (!record || !record.slug) return null;
  const dollars = Number(record.discountAmountDollars) || 0;
  const pct = Number(record.discountPercentage) || 0;
  if (dollars > 0) return { headline: `$${dollars} off`, isFree: false };
  if (pct >= 100) return { headline: "a free report", isFree: true };
  if (pct > 0) return { headline: `${pct}% off`, isFree: false };
  return null;
}

/** True when a stored record has every field downstream surfaces
 *  expect.  Used to detect records left behind by an older shape
 *  (missing dollar amount, etc.) so we know to re-validate
 *  instead of trusting them. */
function isComplete(record) {
  if (!record || !record.slug) return false;
  // Any code — referral or plain promo — must describe an actual
  // discount. A record that can't (v1 schema, partial write) gets
  // re-validated rather than trusted.
  return describeDiscount(record) !== null;
}

/** Capture-and-validate. Called once at app mount: if `?ref=...`
 * is in the URL, validate it server-side and store the record.
 * Idempotent — safe to call on every mount. Returns a Promise that
 * resolves whether or not a code was captured (for tests). */
export async function captureReferralFromQuery() {
  const slug = readFromQuery();
  if (!slug) return null;

  // Skip re-validate ONLY when the cached record is for the same
  // slug AND complete.  Incomplete records (legacy shape, partial
  // write) get re-validated so the banner / modal / auto-apply
  // see the full payload.
  const existing = getStoredReferral();
  if (existing && existing.slug === slug && isComplete(existing)) {
    return existing;
  }

  try {
    const resp = await axios.post(
      `${API_URL}/discount-codes/validate/`,
      { discount_code: slug },
    );
    if (!resp.data) return null;
    const record = {
      slug,
      // The server tells us whether the code is a referral, the
      // dollar amount (or percentage), and whether it's lifetime;
      // we keep all of it for the auto-apply step downstream.
      // The API field is ``discount_amount_dollars`` — a dollar-
      // valued integer — to match Order.amount and BASE_PRICE_*
      // throughout the codebase.  An earlier draft of this file
      // read ``_cents`` and divided by 100; both are wrong and
      // silently dropped the dollar amount, leaving the auto-
      // apply banner blank even though the discount applied
      // server-side.
      isReferral: !!resp.data.is_referral,
      discountAmountDollars: resp.data.discount_amount_dollars || null,
      discountPercentage: resp.data.discount_percentage,
      isLifetime: !!resp.data.is_lifetime,
      capturedAt: new Date().toISOString(),
    };
    setStoredReferral(record);
    return record;
  } catch (_e) {
    // Invalid / expired / over-cap codes 404. Quietly drop —
    // there's no useful recovery; the user can still proceed
    // through the funnel without the discount.
    return null;
  }
}

/** Convenience for the email/checkout step: returns the referral
 * record IF one is stored AND no manual discount has been applied
 * to the in-flight order yet. */
export function readPendingReferral() {
  return getStoredReferral();
}
